import React, { useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { crmErrorMessage } from "../api/crmErrors";
import usePacks from "../hooks/usePacks";
import useRole from "../hooks/useRole";
import CrmPageHeader from "../ui/CrmPageHeader";
import CrmCard from "../ui/CrmCard";
import CrmButton from "../ui/CrmButton";
import CrmSwitch from "../ui/CrmSwitch";
import EmptyState from "../ui/EmptyState";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import PacksTable from "../packs/PacksTable";
import PackFormSheet from "../packs/PackFormSheet";
import ArchivePackDialog from "../packs/ArchivePackDialog";
import { LABEL } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

/**
 * Admin → CRM → Packs (spec Screen 7 / design §E.8): the session-pack catalogue a
 * practice sells online or assigns offline. Read-only for a Helper (`managePacks`) — the
 * table still renders so a Helper can see what exists while assigning one.
 */
export default function CrmPacks() {
  const t = useT("crm");
  const tc = useT("common");
  const { can } = useRole();
  const owner = can("managePacks");
  const [includeArchived, setIncludeArchived] = useState(false);
  const list = usePacks({ includeArchived });
  const [editing, setEditing] = useState(null); // null = closed, {} = create, pack = edit
  const [archiving, setArchiving] = useState(null);

  return (
    <div data-testid={CRM.packs} className="space-y-4">
      <CrmPageHeader
        title={t("packs.title")}
        actions={owner && (
          <CrmButton onClick={() => setEditing({})} data-testid={CRM.packsCreate}>{t("packs.create")}</CrmButton>
        )}
      />
      <CrmCard className="space-y-4">
        <label className="flex items-center gap-2 text-sm text-eh-ink">
          <CrmSwitch checked={includeArchived} onCheckedChange={setIncludeArchived} data-testid={CRM.packsShowArchived} />
          <span className={LABEL + " mb-0"}>{t("packs.showArchived")}</span>
        </label>

        {list.loadError && <CrmLoadFailure error={list.loadError} onRetry={list.reload} testId={CRM.packsError} />}
        {!list.loadError && list.loaded && list.rows.length === 0 && (
          <EmptyState
            testId={CRM.packsEmpty}
            title={t("packs.empty")}
            body={t("packs.emptyBody")}
            action={owner && <CrmButton onClick={() => setEditing({})}>{t("packs.create")}</CrmButton>}
          />
        )}
        {list.rows.length > 0 && (
          <PacksTable
            rows={list.rows}
            canManage={owner}
            onEdit={setEditing}
            onArchive={setArchiving}
            onUnarchive={(pack) => list.unarchive(pack.id)
              .then(() => toast.success(t("packs.unarchived")))
              .catch((e) => toast.error(crmErrorMessage(e, t, tc)))}
          />
        )}
      </CrmCard>

      {editing && (
        <PackFormSheet
          pack={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => list.reload()}
        />
      )}
      {archiving && (
        <ArchivePackDialog
          pack={archiving}
          onClose={() => setArchiving(null)}
          onArchived={(pack) => list.archive(pack.id)}
        />
      )}
    </div>
  );
}
